import { raise } from '@sparkdotfi/common-universal'
import { cva } from 'class-variance-authority'
import { ChevronsRight, ExternalLinkIcon } from 'lucide-react'

import { savingsTokenToAccountType } from '../../common/utils'
import { DepositCTAPanelProps } from '../DepositCTAPanel'
import { Token } from '@/domain/types/Token'
import { AccountSparkRewardsSummary } from '@/features/savings/types'
import { assets } from '@/ui/assets'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { IconStack } from '@/ui/molecules/icon-stack/IconStack'
import { Info } from '@/ui/molecules/info/Info'
import { cn } from '@/ui/utils/style'

export interface DetailsProps {
  description: { text: string; docsLink: string }
  entryTokens: Token[]
  savingsToken: Token
  sparkRewardsSummary: DepositCTAPanelProps['sparkRewardsSummary']
  sparkPointsSummary: DepositCTAPanelProps['sparkPointsSummary']
}

export function Details({
  description,
  entryTokens,
  savingsToken,
  sparkRewardsSummary,
  sparkPointsSummary,
}: DetailsProps) {
  const isDescriptionShort = description.text.length < 150

  const showRewardsPill = import.meta.env.VITE_BASE_MINI_APP !== '1' && sparkRewardsSummary.rewards.length > 0
  const showPointsPill = import.meta.env.VITE_BASE_MINI_APP !== '1' && sparkPointsSummary.multiplier > 0

  return (
    <div className="flex flex-col gap-2 lg:gap-4">
      <div className={cn('flex gap-3', showRewardsPill && showPointsPill && 'flex-col sm:flex-row')}>
        <div className={tokensRouteVariants({ bg: savingsTokenToAccountType(savingsToken) })}>
          <IconStack items={entryTokens} iconClassName="box-content border-2 border-[#1E3B33] rounded-full" />
          <ChevronsRight className="icon-size-xs icon-primary-inverse" />
          <IconStack items={[savingsToken]} iconBorder="transparent" />
        </div>
        {showRewardsPill && <EligibleForRewardPill sparkRewardsSummary={sparkRewardsSummary} />}
        {showPointsPill && <EligibleForSparkPointsPill sparkPointsMultiplier={sparkPointsSummary.multiplier} />}
      </div>
      <div
        className={cn(
          'typography-body-3 max-w-[64ch] text-tertiary',
          isDescriptionShort && 'flex max-w-[48ch] flex-col',
        )}
      >
        {description.text}{' '}
        {import.meta.env.VITE_BASE_MINI_APP !== '1' &&
          (isDescriptionShort ? (
            <ExternalLink to={description.docsLink} className={cn('flex items-center gap-1 text-savings')}>
              Learn more <ExternalLinkIcon className="icon-size-xxs" />
            </ExternalLink>
          ) : (
            <ExternalLink to={description.docsLink} className="text-savings">
              Learn more
            </ExternalLink>
          ))}
      </div>
    </div>
  )
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <div className="typography-label-3 flex w-fit items-center gap-1 rounded-full bg-primary/10 p-1 text-primary-inverse backdrop-blur-sm">
      {children}
    </div>
  )
}

const tokensRouteVariants = cva('flex w-fit items-center gap-1 rounded-full p-0.5', {
  variants: {
    bg: {
      susds: 'bg-[linear-gradient(90deg,rgb(43,79,74,0.2)_0%,rgb(43,79,74)_100%)]',
      susdc: 'bg-[linear-gradient(90deg,rgb(42,78,92,0.2)_0%,rgb(42,78,92)_100%)]',
      sdai: 'bg-[linear-gradient(90deg,rgb(39,81,60,0.2)_0%,rgb(39,81,60)_100%)]',
      stusds: 'bg-[linear-gradient(90deg,rgb(43,79,74,0.2)_0%,rgb(43,79,74)_100%)]',
      spusdc: 'bg-[linear-gradient(90deg,rgb(42,78,92,0.2)_0%,rgb(42,78,92)_100%)]',
      spusdt: 'bg-[linear-gradient(90deg,rgb(43,79,74,0.2)_0%,rgb(43,79,74)_100%)]',
      speth: 'bg-[linear-gradient(90deg,rgb(43,79,74,0.2)_0%,rgb(43,79,74)_100%)]',
      sppyusd: 'bg-[linear-gradient(90deg,rgb(43,79,74,0.2)_0%,rgb(43,79,74)_100%)]',
    },
  },
})

function EligibleForRewardPill({ sparkRewardsSummary }: { sparkRewardsSummary: AccountSparkRewardsSummary }) {
  if (sparkRewardsSummary.rewards.length === 1) {
    const { rewardTokenSymbol, rewardTokenLogo, longDescription } =
      sparkRewardsSummary.rewards[0] ?? raise('No reward found')

    return (
      <Pill>
        <img src={rewardTokenLogo} alt={rewardTokenSymbol} className="size-6" />
        <div>Rewards in {rewardTokenSymbol} Token</div>
        <Info>{longDescription}</Info>
      </Pill>
    )
  }

  return (
    <Pill>
      <img src={assets.page.sparkRewardsCircle} alt={'Spark Rewards'} className="size-6" />
      <div>Eligible for rewards</div>
      <Info>
        Eligible for rewards in the following tokens:{' '}
        {sparkRewardsSummary.rewards.map(({ rewardTokenSymbol }) => rewardTokenSymbol).join(', ')}
      </Info>
    </Pill>
  )
}

interface EligibleForSparkPointsPillProps {
  sparkPointsMultiplier: number
}

function EligibleForSparkPointsPill({ sparkPointsMultiplier }: EligibleForSparkPointsPillProps) {
  return (
    <Pill>
      <img src={assets.points.pointsIcon} alt={'Spark Points'} className="size-6" />
      <div>Eligible for Spark Points</div>
      <Info>Eligible for Spark Points with a multiplier of {sparkPointsMultiplier}x.</Info>
    </Pill>
  )
}
